import React, { useState, useEffect, useRef } from "react";
import CytoscapeComponent from "react-cytoscapejs";
import {
  initialNodeStyle,
  initialUndirectedWeightedEdgeStyle,
} from "./GraphStyle";

import "./CytoscapeGraph.css";
import Prims from "./Prims";
import Kruskals from "./Kruskals";

const UWG = ({ algorithmType }) => {
  const cyStyle = {
    width: "100%",
    height: "100%",
  };

  const resetState = () => {
    setNodes([]);
    setEdges([]);
    setVisited([]);
    setQueue([]);
    setAdjacencyList({});
    setGenerateGraph(false);
    setEdgeWeights({});
  };

  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [visited, setVisited] = useState([]);
  const [queue, setQueue] = useState([]);
  const [cytoscapeReady, setCytoscapeReady] = useState(false);

  const [numNodes, setNumNodes] = useState(2); // Number of nodes
  const [adjacencyList, setAdjacencyList] = useState({}); // Adjacency list
  const [generateGraph, setGenerateGraph] = useState(false);
  const [edgeWeights, setEdgeWeights] = useState({});
  const [selectedStartingNode, setSelectedStartingNode] = useState(1);

  // Create a ref to store the Cytoscape instance
  const cytoscapeRef = useRef(null);

  useEffect(() => {
    if (generateGraph) {
      // Generate graph elements
      const generatedElements = generateGraphElements(numNodes, adjacencyList);
      setNodes(generatedElements.nodes);
      setEdges(generatedElements.edges);
      if (cytoscapeRef.current) {
        cytoscapeRef.current.fit();
      }
    }
  }, [generateGraph, numNodes, adjacencyList]);

  const generateGraphElements = (numNodes, adjacencyList) => {
    const generatedNodes = [];
    const generatedEdges = [];

    // Calculate the number of rows and columns for the grid
    const numRows = Math.ceil(Math.sqrt(numNodes));
    const numCols = Math.ceil(numNodes / numRows);

    // Calculate the spacing between nodes
    const spacingX = 300 / numCols;
    const spacingY = 300 / numRows;

    // Generate nodes in a grid
    for (let row = 0; row < numRows; row++) {
      for (let col = 0; col < numCols; col++) {
        const nodeId = row * numCols + col + 1;
        if (nodeId <= numNodes) {
          const positionY = row * spacingY + (col % 2 === 0 ? 0 : spacingY / 2);
          generatedNodes.push({
            data: { id: `node${nodeId}`, label: `${nodeId}` },
            position: { x: col * spacingX, y: positionY },
          });
        }
      }
    }

    for (const sourceNode in adjacencyList) {
      const targetNodes = adjacencyList[sourceNode];
      targetNodes.forEach((targetNode) => {
        if (sourceNode < targetNode) {
          const edgeId = `${sourceNode}-${targetNode}`;
          const weight = edgeWeights[edgeId] || "";
          generatedEdges.push({
            data: {
              id: edgeId,
              source: `node${sourceNode}`,
              target: `node${targetNode}`,
              weight: weight,
            },
          });
        }
      });
    }
    return { nodes: generatedNodes, edges: generatedEdges };
  };

  const handleNumNodesChange = (e) => {
    const newNumNodes = parseInt(e.target.value);

    if (newNumNodes >= 2 && newNumNodes <= 10) {
      setNumNodes(newNumNodes);
      if (selectedStartingNode > newNumNodes) {
        setSelectedStartingNode(newNumNodes);
      }
    } else if (newNumNodes < 2) {
      setNumNodes(2); // Minimum value of 2
    } else if (newNumNodes > 10) {
      setNumNodes(10); // Maximum value of 10
    }
  };

  const handleAdjacencyChange = (node, adjacentNode) => {
    const updatedAdjacencyList = { ...adjacencyList };

    if (!updatedAdjacencyList[node]) {
      updatedAdjacencyList[node] = [];
    }
    if (!updatedAdjacencyList[adjacentNode]) {
      updatedAdjacencyList[adjacentNode] = [];
    }
    const nodeIndex = updatedAdjacencyList[node].indexOf(adjacentNode);
    const adjacentNodeIndex = updatedAdjacencyList[adjacentNode].indexOf(node);

    if (nodeIndex !== -1) {
      updatedAdjacencyList[node].splice(nodeIndex, 1);
      updatedAdjacencyList[adjacentNode].splice(adjacentNodeIndex, 1);
      setEdgeWeights((prevEdgeWeights) => {
        const newEdgeWeights = { ...prevEdgeWeights };
        const edgeId = `${node}-${adjacentNode}`;
        const reversedEdgeId = `${adjacentNode}-${node}`;
        delete newEdgeWeights[edgeId];
        delete newEdgeWeights[reversedEdgeId];
        return newEdgeWeights;
      });
    } else {
      updatedAdjacencyList[node].push(adjacentNode);
      updatedAdjacencyList[adjacentNode].push(node);
    }

    setAdjacencyList(updatedAdjacencyList);
  };

  const allEdgesWeighted = () => {
    for (const sourceNode in adjacencyList) {
      const targetNodes = adjacencyList[sourceNode];
      for (const targetNode of targetNodes) {
        const edgeId = `${sourceNode}-${targetNode}`;
        const reversedEdgeId = `${targetNode}-${sourceNode}`;

        // Check if the weight has been entered for either direction
        if (!edgeWeights[edgeId] && !edgeWeights[reversedEdgeId]) {
          return false;
        }
      }
    }
    return true;
  };

  const handleEdgeWeightChange = (edgeId, weight) => {
    setEdgeWeights((prevEdgeWeights) => {
      // Update the edge weight for the provided edge
      const newEdgeWeights = { ...prevEdgeWeights };
      newEdgeWeights[edgeId] = weight;

      // Split the edgeId into source and target nodes
      const [sourceNode, targetNode] = edgeId.split("-");

      // Create a copy of the adjacency list
      const updatedAdjacencyList = { ...adjacencyList };

      // Update the adjacency list for both directions
      if (!updatedAdjacencyList[sourceNode]) {
        updatedAdjacencyList[sourceNode] = [];
      }

      if (!updatedAdjacencyList[targetNode]) {
        updatedAdjacencyList[targetNode] = [];
      }

      if (!updatedAdjacencyList[sourceNode].includes(targetNode)) {
        updatedAdjacencyList[sourceNode].push(targetNode);
      }

      if (!updatedAdjacencyList[targetNode].includes(sourceNode)) {
        updatedAdjacencyList[targetNode].push(sourceNode);
      }

      // Update the weights for both directions
      newEdgeWeights[`${targetNode}-${sourceNode}`] = weight;

      setAdjacencyList(updatedAdjacencyList);
      return newEdgeWeights;
    });
  };

  const handleGenerateGraph = () => {
    setNodes([]);
    setEdges([]);
    if (!allEdgesWeighted()) {
      alert("Please enter weights for all edges before generating the graph.");
      return;
    }
    if (selectedStartingNode > numNodes) {
      setSelectedStartingNode(numNodes);
    }
    setGenerateGraph((prevGenerateGraph) => {
      if (!prevGenerateGraph) {
        setNodes([]);
        setEdges([]);

        const generatedElements = generateGraphElements(
          numNodes,
          adjacencyList
        );
        setNodes(generatedElements.nodes);
        setEdges(generatedElements.edges);
      }
      return !prevGenerateGraph;
    });
  };

  const handleCytoscapeReady = (cy) => {
    if (cy) {
      cy.style()
        .fromJson([
          {
            selector: "node",
            style: {
              ...initialNodeStyle,
            },
          },
          {
            selector: "edge",
            style: {
              ...initialUndirectedWeightedEdgeStyle,
            },
          },
        ])
        .update();

      setCytoscapeReady(true);
    }
  };

  return (
    <div className="graph-container">
      <div className="left-column">
        <div className="graph-options">
          {algorithmType === "prims" && (
            <label className="graph-option">
              <b>Go ahead and create an undirected weighted connected graph.</b>
            </label>
          )}
        </div>
        <div className="node-input">
          <label className="node-label">Number of Nodes:</label>
          <input
            className="node-input-field"
            type="number"
            value={numNodes}
            onChange={handleNumNodesChange}
            min="2"
            max="10"
          />
        </div>
        <div className="node-input">
          {algorithmType === "kruskals" && (
            <label className="node-label">
              Edges will be sorted in ascending order of weights.
            </label>
          )}
          {algorithmType === "prims" && (
            <label className="node-label">Select Starting Node:</label>
          )}
          {algorithmType === "prims" && (
            <input
              className="node-input-field"
              type="number"
              value={selectedStartingNode}
              onChange={(e) =>
                setSelectedStartingNode(parseInt(e.target.value))
              }
              min="1"
              max={numNodes}
            />
          )}
        </div>
        <div className="adjacency-list">
          <label className="adjacency-label">Adjacency List:</label>
          <ul className="adjacency-list-ul">
            {Array.from({ length: numNodes }, (_, i) => i + 1).map((node) => (
              <li key={node}>
                <b>Node {node}:</b>&nbsp;&nbsp;&nbsp;
                {Array.from({ length: numNodes }, (_, i) => i + 1)
                  .filter((adjNode) => adjNode !== node)
                  .map((adjNode) => (
                    <div key={adjNode} className="adjacency-list-entry">
                      <input
                        type="checkbox"
                        checked={(adjacencyList[node] || []).includes(adjNode)}
                        onChange={() => handleAdjacencyChange(node, adjNode)}
                      />
                      {adjNode}
                      &nbsp;&nbsp;&nbsp;
                      <input
                        type="number"
                        placeholder="Weight"
                        value={edgeWeights[`${node}-${adjNode}`] || ""}
                        onChange={(e) =>
                          handleEdgeWeightChange(
                            `${node}-${adjNode}`,
                            e.target.value
                          )
                        }
                        style={{ width: "69px" }}
                        disabled={!adjacencyList[node]?.includes(adjNode)}
                      />
                    </div>
                  ))}
              </li>
            ))}
          </ul>
        </div>
        <div className="graph-controls">
          <button
            className="generate-reset-button"
            onClick={handleGenerateGraph}
          >
            Generate Graph
          </button>
          &nbsp;&nbsp;
          <button className="generate-reset-button" onClick={resetState}>
            Reset Graph
          </button>
        </div>
      </div>
      <div className="right-column">
        <div className="cytoscape-container ">
          <CytoscapeComponent
            elements={nodes.concat(edges)}
            style={cyStyle}
            cy={(cy) => {
              cy.userZoomingEnabled(false);
              cytoscapeRef.current = cy;
              handleCytoscapeReady(cy);
            }}
          />
        </div>
        {algorithmType === "prims" && (
          <Prims
            cytoscape={cytoscapeRef.current}
            nodes={nodes}
            visited={visited}
            queue={queue}
            setVisited={setVisited}
            setQueue={setQueue}
            startingNode={selectedStartingNode}
          />
        )}
        {algorithmType === "kruskals" && (
          <Kruskals
            cytoscape={cytoscapeRef.current}
            nodes={nodes}
            visited={visited}
            queue={queue}
            setVisited={setVisited}
            setQueue={setQueue}
          />
        )}
      </div>
    </div>
  );
};

export default UWG;
